/**
 * Authentication Context - Global auth state management
 * 
 * SECURITY: Uses httpOnly cookies for token storage (JavaScript cannot access)
 * CSRF Protection: Fetches and includes CSRF tokens on all state-changing requests
 */

import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef, ReactNode } from 'react'
import { QueryClientContext } from '@tanstack/react-query'
import { api, getCsrfToken } from '../utils/api'
import { sanitizeUser } from '../utils/sanitize'

// User interface
export interface User {
  id: string
  email: string
  firstName: string
  lastName: string
  displayName?: string
  role: string
  organizationId: string
  organizationName: string
  /** Store locale (BCP 47) for receipts, date formatting, etc. */
  organizationLocale?: string
  /** ISO currency for the organisation listing — minimum price floors in metadata use this unit. */
  organizationCurrency?: string
  subscriptionTier?: string
  subscriptionStatus?: string
  subscriptionEndsAt?: string | null
  isOrganizationSuspended?: boolean
  mfaEnabled?: boolean
  isEmailVerified?: boolean
  dashboardPreferences?: {
    showLowStockWarnings?: boolean
    showDraftTradeWarnings?: boolean
  }
}

// Auth context interface
interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  authWarning: { message: string; emailNotVerified?: boolean; resendEndpoint?: string } | null
  login: (email: string, password: string, rememberMe?: boolean) => Promise<{ mfaRequired?: boolean }>
  verifyMfa: (code: string, rememberMe?: boolean) => Promise<void>
  logout: () => Promise<void>
  refreshAuth: () => Promise<void>
  dismissWarning: () => void
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Props interface
interface AuthProviderProps {
  children: ReactNode
}

/**
 * AuthProvider Component
 * Wraps the app and provides authentication state/methods
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [authWarning, setAuthWarning] = useState<{ message: string; emailNotVerified?: boolean; resendEndpoint?: string } | null>(null)
  const rememberedSessionRefreshRef = useRef<Promise<void> | null>(null)
  const lastRememberedSessionRefreshAtRef = useRef(0)

  // Check if user is authenticated
  const isAuthenticated = user !== null

  // Drop every cached query when the signed-in identity changes or goes away. Query keys like
  // ['inventory', 'list', filters] carry no user or org, so on a shared POS terminal the next
  // person to sign in was shown the previous store's inventory, sales and dashboard until each
  // query happened to refetch. One effect covers every logout path (explicit, inactivity,
  // failed refresh) instead of each remembering to do it. Read via context so a test that
  // renders this provider without a QueryClient still works.
  const queryClient = useContext(QueryClientContext)
  const identity = user ? `${user.id}:${user.organizationId}` : null
  const previousIdentityRef = useRef<string | null>(null)
  useEffect(() => {
    const previous = previousIdentityRef.current
    previousIdentityRef.current = identity
    if (previous !== null && previous !== identity) {
      queryClient?.clear()
    }
  }, [identity, queryClient])

  const dismissWarning = useCallback(() => {
    setAuthWarning(null)
  }, [])

  const hasRememberedSession = useCallback(() => {
    if (typeof document === 'undefined') {
      return false
    }

    return document.cookie
      .split(';')
      .map(part => part.trim())
      .some(part => part === 'rememberSession=1')
  }, [])

  /**
   * Initialize auth state on mount
   * Fetch CSRF token and verify authentication via cookie
   * Includes timeout to prevent infinite loading state
   */
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null
    let isMounted = true

    const initAuth = async () => {
      // Set a timeout to ensure we never get stuck loading
      timeoutId = setTimeout(() => {
        if (isMounted && isLoading) {
          console.warn('Auth initialization timed out')
          setIsLoading(false)
        }
      }, 15000) // 15 second timeout

      try {
        // Fetch CSRF token first (required for subsequent requests)
        await getCsrfToken()
        
        // Try to verify existing session (cookie-based). 
        // We use X-Auth-No-Redirect to prevent the interceptor from bouncing to /login if refresh also fails.
        const response = await api.get('/auth/me', { headers: { 'X-Auth-No-Redirect': 'true' } })
        
        if (isMounted && response.data.success) {
          const { user: userData, organization } = response.data.data
          
          // Sanitize user data to prevent XSS
          setUser(sanitizeUser({
            id: userData.id,
            email: userData.email,
            firstName: userData.firstName,
            lastName: userData.lastName,
            displayName: userData.displayName,
            role: userData.role,
            organizationId: userData.organizationId,
            organizationName: organization?.name || '',
            organizationLocale: organization?.locale,
            organizationCurrency: organization?.currency,
            subscriptionTier: organization?.subscriptionTier,
            subscriptionStatus: organization?.subscriptionStatus,
            subscriptionEndsAt: organization?.subscriptionEndsAt ?? null,
            isOrganizationSuspended: !!organization?.isSuspended,
            dashboardPreferences: userData.metadata?.dashboardPreferences
          }))
        }
      } catch (error) {
        // No valid session - user needs to login
        console.debug('No active session')
      } finally {
        if (timeoutId) {
          clearTimeout(timeoutId)
        }
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    initAuth()

    return () => {
      isMounted = false
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [])

  /**
   * Inactivity auto-logout (8 hours)
   * 
   * Tracks mouse, keyboard, touch, and scroll events as "activity".
   * If no activity is detected for 8 hours, automatically logs the user out.
   * This is designed for in-store kiosk/POS use where stores stay logged in
   * during business hours but should auto-logout after closing.
   */
  const INACTIVITY_TIMEOUT_MS = 8 * 60 * 60 * 1000 // 8 hours
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null)

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current)
    }
    // Only set timer if user is authenticated
    if (user) {
      inactivityTimerRef.current = setTimeout(() => {
        console.warn('Auto-logout: 8 hours of inactivity')
        // Clear local state and call logout API
        api.post('/auth/logout').catch(() => {})
        setUser(null)
      }, INACTIVITY_TIMEOUT_MS)
    }
  }, [user])

  useEffect(() => {
    if (!user) {
      // Not logged in — clear any existing timer
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current)
        inactivityTimerRef.current = null
      }
      return
    }

    // Start the inactivity timer
    resetInactivityTimer()

    const activityEvents = ['mousedown', 'keydown', 'touchstart', 'scroll', 'mousemove']
    // Throttle: only reset timer at most once per 60 seconds for performance
    let lastReset = Date.now()
    const handleActivity = () => {
      const now = Date.now()
      if (now - lastReset > 60_000) {
        lastReset = now
        resetInactivityTimer()
      }
    }

    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true })
    })

    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity)
      })
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current)
        inactivityTimerRef.current = null
      }
    }
  }, [user, resetInactivityTimer])

  /**
   * Login function
   * Tokens are stored in httpOnly cookies by the backend
   */
  const login = useCallback(async (email: string, password: string, rememberMe: boolean = false) => {
    try {
      // Get fresh CSRF token before login
      await getCsrfToken()
      
      const response = await api.post('/auth/login', {
        email,
        password,
        rememberMe
      })

      const data = response.data.data

      // Check for auth warnings (e.g., email not verified)
      if (data?.warning) {
        setAuthWarning(data.warning)
      }

      // MFA gate: backend sets a short-lived mfaToken cookie and tells us MFA is required.
      if (data?.mfaRequired) {
        return { mfaRequired: true }
      }

      const { user: userData, organisation } = data

      // Sanitize user data before storing
      // Note: Organisation name comes from the separate organisation object, not userData
      setUser(sanitizeUser({
        id: userData.id,
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        displayName: userData.displayName,
        role: userData.role,
        organizationId: userData.organizationId,
        organizationName: organisation?.name || '',
        organizationLocale: organisation?.locale,
        organizationCurrency: organisation?.currency,
        subscriptionTier: organisation?.subscriptionTier,
        subscriptionStatus: organisation?.subscriptionStatus,
        subscriptionEndsAt: organisation?.subscriptionEndsAt ?? null,
        isOrganizationSuspended: !!organisation?.isSuspended,
        mfaEnabled: !!userData.mfaEnabled,
        isEmailVerified: !!userData.isEmailVerified,
        dashboardPreferences: userData.metadata?.dashboardPreferences
      }))

      // Verify that auth cookies were accepted/sent (prevents "logged-in" UI with 401s everywhere).
      try {
        const verify = await api.get('/auth/me')
        if (!verify.data?.success) {
          throw new Error('Session verification failed')
        }
      } catch {
        setUser(null)
        throw new Error(
          'Login succeeded but your session could not be established. Please allow cookies for this site and try again.'
        )
      }
      return { mfaRequired: false }
    } catch (error: any) {
      // Re-throw error for component to handle
      throw error
    }
  }, [])

  /**
   * Complete MFA login using the httpOnly mfaToken cookie.
   */
  const verifyMfa = useCallback(async (code: string, rememberMe: boolean = false) => {
    // Ensure CSRF token is available
    await getCsrfToken()

    const response = await api.post('/auth/mfa/verify', { code, rememberMe })
    const { user: userData, organisation } = response.data.data

    setUser(sanitizeUser({
      id: userData.id,
      email: userData.email,
      firstName: userData.firstName,
      lastName: userData.lastName,
      displayName: userData.displayName,
      role: userData.role,
      organizationId: userData.organizationId,
      organizationName: organisation?.name || '',
      organizationLocale: organisation?.locale,
      organizationCurrency: organisation?.currency,
      subscriptionTier: organisation?.subscriptionTier,
      subscriptionStatus: organisation?.subscriptionStatus,
      subscriptionEndsAt: organisation?.subscriptionEndsAt ?? null,
      isOrganizationSuspended: !!organisation?.isSuspended,
      mfaEnabled: !!userData.mfaEnabled,
      dashboardPreferences: userData.metadata?.dashboardPreferences
    }))
  }, [])

  /**
   * Logout function
   * Clears httpOnly cookies on backend and local state
   */
  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout')
    } catch (error) {
      console.error('Logout API call failed:', error)
    } finally {
      if (typeof localStorage !== 'undefined') {
        const keys: string[] = []
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i)
          if (k?.startsWith('tcgeasy_active_trade_id')) keys.push(k)
        }
        for (const k of keys) localStorage.removeItem(k)
      }
      // Clear local state regardless of API call result
      setUser(null)
    }
  }, [])

  const refreshRememberedSession = useCallback(async () => {
    if (!user || !hasRememberedSession()) {
      return
    }

    const now = Date.now()
    if (now - lastRememberedSessionRefreshAtRef.current < 12 * 60 * 60 * 1000) {
      return
    }

    if (rememberedSessionRefreshRef.current) {
      await rememberedSessionRefreshRef.current
      return
    }

    rememberedSessionRefreshRef.current = api.post('/auth/refresh', {})
      .then(() => {
        lastRememberedSessionRefreshAtRef.current = Date.now()
      })
      .catch((error) => {
        console.debug('Remembered session refresh skipped:', error)
      })
      .finally(() => {
        rememberedSessionRefreshRef.current = null
      })

    await rememberedSessionRefreshRef.current
  }, [hasRememberedSession, user])

  useEffect(() => {
    if (!user || !hasRememberedSession()) {
      return
    }

    let lastActivityAttempt = 0
    const maybeRefreshRememberedSession = () => {
      const now = Date.now()
      if (now - lastActivityAttempt < 60_000) {
        return
      }

      lastActivityAttempt = now
      void refreshRememberedSession()
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        maybeRefreshRememberedSession()
      }
    }

    window.addEventListener('focus', maybeRefreshRememberedSession)
    window.addEventListener('mousedown', maybeRefreshRememberedSession, { passive: true })
    window.addEventListener('keydown', maybeRefreshRememberedSession)
    window.addEventListener('touchstart', maybeRefreshRememberedSession, { passive: true })
    document.addEventListener('visibilitychange', handleVisibilityChange)

    maybeRefreshRememberedSession()

    return () => {
      window.removeEventListener('focus', maybeRefreshRememberedSession)
      window.removeEventListener('mousedown', maybeRefreshRememberedSession)
      window.removeEventListener('keydown', maybeRefreshRememberedSession)
      window.removeEventListener('touchstart', maybeRefreshRememberedSession)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [hasRememberedSession, refreshRememberedSession, user])

  /**
   * Refresh authentication state
   * Useful for updating user data after profile changes
   */
  const refreshAuth = useCallback(async () => {
    try {
      const response = await api.get('/auth/me')
      const { user: userData, organization } = response.data.data
      
      setUser(sanitizeUser({
        id: userData.id,
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        displayName: userData.displayName,
        role: userData.role,
        organizationId: userData.organizationId,
        organizationName: organization?.name || '',
        organizationLocale: organization?.locale,
        organizationCurrency: organization?.currency,
        subscriptionTier: organization?.subscriptionTier,
        subscriptionStatus: organization?.subscriptionStatus,
        subscriptionEndsAt: organization?.subscriptionEndsAt ?? null,
        isOrganizationSuspended: !!organization?.isSuspended,
        mfaEnabled: !!userData.mfaEnabled,
        dashboardPreferences: userData.metadata?.dashboardPreferences
      }))
    } catch (error) {
      console.error('Failed to refresh auth:', error)
      // Session expired or invalid
      setUser(null)
    }
  }, [])

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo<AuthContextType>(() => ({
    user,
    isAuthenticated,
    isLoading,
    authWarning,
    login,
    verifyMfa,
    logout,
    refreshAuth,
    dismissWarning
  }), [user, isAuthenticated, isLoading, authWarning, login, verifyMfa, logout, refreshAuth, dismissWarning])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/**
 * useAuth hook
 * Access authentication context from any component
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  
  return context
}

/**
 * The auth context when one is mounted, else undefined. For providers that sit
 * under AuthProvider in the app but are also rendered alone (prerender, tests).
 */
export const useOptionalAuth = (): AuthContextType | undefined => useContext(AuthContext)
